import React, { useState, useEffect, useMemo } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth, hasPermission, isAdmin } from "../context/AuthContext";
import { formatIndiaDate } from "../lib/indiaTime";
import { navGroupsForUser, flattenNavGroups, hasSingleNavGroup } from "../lib/permissions";
import BrandLogo from "./BrandLogo";
import { IS_DEMO } from "../lib/demo";
import { pageTitleForPath, pickBottomModuleItems, pickBottomNavItems } from "../lib/appNav";
import { ROLE_BADGE } from "../lib/roleMeta";
import AppBottomNav from "./AppBottomNav";
import MobileModuleMenu from "./mobile/MobileModuleMenu";
import MobileSubTabBar, { findActiveNavGroup } from "./mobile/MobileSubTabBar";
import { useMobileNavBadges } from "../hooks/useMobileNavBadges";
import {
  LayoutDashboard,
  Truck,
  Scale,
  FileText,
  ClipboardList,
  Layers,
  FlaskConical,
  PackageOpen,
  Boxes,
  Factory,
  Receipt,
  Building2,
  Users,
  ListChecks,
  LogOut,
  ChevronRight,
  ChevronDown,
  ScrollText,
  BarChart3,
  Search,
  Plus,
  PanelLeftClose,
  PanelLeftOpen,
  KeyRound,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import CommandPalette from "./CommandPalette";
import NotificationsInbox from "./NotificationsInbox";
import ThemeToggle from "./ThemeToggle";
import AccountSettingsDialog from "./AccountSettingsDialog";
import { useTallyApproval } from "../context/TallyApprovalContext";
import { cn } from "../lib/utils";

const SIDEBAR_COLLAPSED_KEY = "bansal_sidebar_collapsed";

function readSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

const ICONS = {
  dashboard: LayoutDashboard,
  gate: Truck,
  weighbridge: Scale,
  contracts: FileText,
  purchases: ClipboardList,
  stack_samples: Layers,
  lab: FlaskConical,
  unloading: PackageOpen,
  stacks: Boxes,
  production: Factory,
  accounts: Receipt,
  accounts_manager: Receipt,
  parties: Building2,
  users: Users,
  audit: ScrollText,
  lookups: ListChecks,
  reports: BarChart3,
};

const SHORT_LABELS = {
  "/": "Home",
  "/weighbridge": "Weigh",
  "/weighbridge/inward": "Inward",
  "/weighbridge/outward": "Outward",
  "/weighbridge/register": "Register",
  "/lab/truck": "Truck",
  "/lab/composite": "Composite",
  "/lab/plant": "Plant",
  "/lab/stacks": "Stack",
  "/gate/procurement": "Inward",
  "/gate/outward": "Outward",
  "/gate/visitors": "Visitor",
  "/gate/store": "Store",
  "/store/grn": "Store GRN",
  "/stack-sample-intake": "Samples",
  "/stack-sample-approval": "Approve",
  "/dropdown-choices": "Lookups",
  "/paddy-quality-lab": "Lab limits",
  "/production/boiler": "Boiler",
  "/production/boiler/dashboard": "Boiler dash",
  "/production/boiler/register": "Register",
  "/production/parboiled": "Parboiled",
  "/production/parboiled/dashboard": "Parboiled dash",
  "/production/milling": "Milling",
  "/production/milling/dashboard": "Milling dash",
  "/production/team": "Team",
  "/production/packing-register": "Packing",
  "/production": "Yield",
  "/sales-contracts": "Contracts",
  "/sales/link": "Link",
  "/sales/loading": "Loading",
  "/sales/vouchers": "Voucher",
  "/sales/reports": "Reporting",
  "/contracts": "Contracts",
  "/unloading": "Unload",
  "/purchases": "Purchases",
  "/purchase/vouchers": "Voucher",
  "/purchase/reports": "Reporting",
  "/accounts": "Accounts",
  "/parties": "Parties",
  "/users": "Users",
  "/audit-logs": "Audit",
};

function navItemActive(pathname, to) {
  if (to === "/") return pathname === "/";
  if (pathname === to) return true;
  if (pathname.startsWith(`${to}/`)) {
    const rest = pathname.slice(to.length + 1);
    if (rest === "dashboard" || rest.startsWith("dashboard/")) return false;
    return true;
  }
  return false;
}

function groupIsActive(pathname, items) {
  return items.some((item) => navItemActive(pathname, item.to));
}

const desktopNavClass = ({ isActive }, sub = false) =>
  `group relative flex items-center gap-3 rounded-md text-[13px] transition-colors min-h-11 ${
    sub ? "pl-9 pr-3 py-2" : "px-3 py-2.5"
  } ${
    isActive
      ? "bg-white/[0.06] text-bansal-gold font-medium"
      : "text-bansal-ivory/65 hover:text-bansal-ivory hover:bg-white/5"
  }`;

const desktopNavIconClass = ({ isActive }) =>
  `group relative flex items-center justify-center rounded-md transition-colors h-11 w-11 ${
    isActive
      ? "bg-white/[0.08] text-bansal-gold"
      : "text-bansal-ivory/70 hover:text-bansal-ivory hover:bg-white/5"
  }`;

const mobileNavClass = ({ isActive }, sub = false) =>
  `flex items-center gap-3 w-full rounded-xl text-sm transition-colors min-h-12 ${
    sub ? "pl-8 pr-3 py-2.5" : "px-3 py-3"
  } ${
    isActive
      ? "bg-bansal-sage/25 text-bansal-gold font-medium"
      : "text-bansal-ivory/85 active:bg-white/10"
  }`;

function CollapsedNavRail({ items }) {
  const siblingTos = items.map((n) => n.to);
  return (
    <TooltipProvider delayDuration={120}>
      <ul className="flex flex-col items-center gap-1 px-1.5 py-2">
        {items.map((n) => (
          <li key={n.to}>
            <Tooltip>
              <TooltipTrigger asChild>
                <NavLink
                  to={n.to}
                  end={navLinkExactEnd(n.to, siblingTos)}
                  data-testid={`nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
                  className={desktopNavIconClass}
                  aria-label={n.label}
                >
                  <n.icon className="h-[18px] w-[18px] shrink-0 opacity-90" />
                </NavLink>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                className="bg-popover text-popover-foreground border border-border shadow-md"
              >
                {n.label}
              </TooltipContent>
            </Tooltip>
          </li>
        ))}
      </ul>
    </TooltipProvider>
  );
}

function UserAvatar({ user, rb, size = "md" }) {
  const dim = size === "sm" ? "h-9 w-9 text-sm" : "h-10 w-10 text-base";
  return (
    <div className={`${dim} shrink-0 rounded-full grid place-items-center font-serif ${rb.bg} ${rb.text}`}>
      {user.name?.[0] || "U"}
    </div>
  );
}

function navLinkExactEnd(to, siblingTos) {
  if (to === "/") return true;
  return siblingTos.some((other) => other !== to && other.startsWith(`${to}/`));
}

function NavSubList({ items, className, onItemClick, linkClass, sub = false }) {
  const siblingTos = items.map((n) => n.to);
  return (
    <ul className={className || "space-y-0.5"}>
      {items.map((n) => (
        <li key={n.to}>
          <NavLink
            to={n.to}
            end={navLinkExactEnd(n.to, siblingTos)}
            onClick={onItemClick}
            data-testid={`nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
            className={(props) => linkClass(props, sub)}
          >
            {({ isActive }) => (
              <>
                {!onItemClick && (
                  <span
                    aria-hidden
                    className={`absolute left-0 top-2 bottom-2 w-[2px] rounded-r-full transition-colors ${
                      isActive ? "bg-bansal-gold" : "bg-transparent group-hover:bg-bansal-sage/40"
                    }`}
                  />
                )}
                <n.icon className={`${onItemClick ? "h-5 w-5" : sub ? "h-4 w-4" : "h-[18px] w-[18px]"} shrink-0 opacity-90`} />
                <span className="flex-1 truncate leading-snug">{n.label}</span>
                {n.badge ? (
                  <span className="ml-1 min-w-[1.15rem] h-5 px-1 rounded-full bg-bansal-gold/90 text-bansal-ink text-[10px] font-semibold grid place-items-center">
                    {n.badge}
                  </span>
                ) : null}
                {onItemClick ? <ChevronRight className="h-4 w-4 shrink-0 opacity-35" aria-hidden /> : null}
              </>
            )}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function NavGroupList({ groups, pathname, linkClass, onItemClick, mobile = false }) {
  if (groups.length === 1) {
    return (
      <NavSubList
        items={groups[0].items}
        linkClass={linkClass}
        onItemClick={onItemClick}
        sub={false}
        className={mobile ? "space-y-1" : "space-y-0.5"}
      />
    );
  }

  return (
    <div className="space-y-1">
      {groups.map((group) => (
        <NavGroupSection
          key={group.id}
          group={group}
          pathname={pathname}
          linkClass={linkClass}
          onItemClick={onItemClick}
          mobile={mobile}
        />
      ))}
    </div>
  );
}

function NavGroupSection({ group, pathname, linkClass, onItemClick, mobile }) {
  const active = groupIsActive(pathname, group.items);
  const [open, setOpen] = useState(active || group.items.length === 1);

  useEffect(() => {
    if (active) setOpen(true);
  }, [active, pathname]);

  const isSingle = group.items.length === 1;

  if (isSingle) {
    const item = { ...group.items[0], icon: group.items[0].icon };
    return (
      <div className="mb-2">
        <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-bansal-ivory/40 font-medium">
          {group.label}
        </div>
        <NavSubList items={[item]} linkClass={linkClass} onItemClick={onItemClick} sub={false} />
      </div>
    );
  }

  return (
    <div className="mb-2" data-testid={`nav-group-${group.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center gap-2 px-3 py-2 rounded-md text-left transition-colors min-h-10 ${
          active
            ? "text-bansal-gold bg-white/[0.04]"
            : "text-bansal-ivory/55 hover:text-bansal-ivory/80 hover:bg-white/[0.03]"
        }`}
        aria-expanded={open}
      >
        <span className="text-[10px] uppercase tracking-[0.22em] font-semibold flex-1">{group.label}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 opacity-60 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {open && (
        <div className={`mt-0.5 ${mobile ? "ml-1 border-l border-white/10" : "ml-2 border-l border-white/[0.08]"}`}>
          <NavSubList
            items={group.items}
            linkClass={linkClass}
            onItemClick={onItemClick}
            sub
            className="space-y-0.5 py-0.5"
          />
        </div>
      )}
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { pendingCount: tallyApprovalPending } = useTallyApproval();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const rb = ROLE_BADGE[user?.role] || ROLE_BADGE.manager;
  const pageTitle = pageTitleForPath(location.pathname, user?.role);

  const navGroups = useMemo(
    () => (user ? navGroupsForUser(user, { hasPermission, isAdmin }) : []).map((group) => ({
      ...group,
      items: group.items.map((n) => ({
        ...n,
        icon: ICONS[n.iconKey] || LayoutDashboard,
        shortLabel: SHORT_LABELS[n.to],
        ...(n.to === "/accounts/tally-approvals" && tallyApprovalPending > 0
          ? {
              label: `Tally approvals (${tallyApprovalPending})`,
              badge: tallyApprovalPending,
            }
          : {}),
      })),
    })),
    [user, tallyApprovalPending],
  );

  const visibleNav = useMemo(() => flattenNavGroups(navGroups), [navGroups]);

  const quickActions = useMemo(() => {
    if (!user) return [];
    const list = [];
    if (hasPermission(user, "page.gate")) list.push({ to: "/gate/procurement", label: "New gate pass", icon: Truck });
    if (hasPermission(user, "page.weighbridge")) list.push({ to: "/weighbridge/inward", label: "Inward weighment", icon: Scale });
    if (hasPermission(user, "page.parties")) list.push({ to: "/parties/new", label: "New party", icon: Building2 });
    if (hasPermission(user, "page.sales_contracts")) list.push({ to: "/sales-contracts", label: "New sales contract", icon: FileText });
    if (hasPermission(user, "page.contracts")) list.push({ to: "/contracts", label: "Purchase contracts", icon: ClipboardList });
    return list;
  }, [user]);

  const singleModuleNav = hasSingleNavGroup(navGroups);
  const activeNavGroup = useMemo(
    () => findActiveNavGroup(navGroups, location.pathname),
    [navGroups, location.pathname],
  );
  const navBadges = useMobileNavBadges(user, singleModuleNav);
  const directTabs = useMemo(() => {
    if (!singleModuleNav) return [];
    return (navGroups[0]?.items || []).map((item) => ({
      ...item,
      badge: navBadges.byPath?.[item.to] ?? 0,
    }));
  }, [navGroups, singleModuleNav, navBadges]);

  if (!user) return null;

  const { home: rawHome, quick: rawQuick } = singleModuleNav
    ? pickBottomNavItems(visibleNav, 3, user?.role)
    : pickBottomModuleItems(navGroups, 3, user?.role);
  const withIcon = (item) => (item ? { ...item, icon: item.icon || ICONS[item.iconKey] || LayoutDashboard } : null);
  const home = withIcon(rawHome);
  const quickNav = rawQuick.map((item) => withIcon(item));
  const closeMobile = () => setMobileOpen(false);
  const toggleSidebar = () => setSidebarCollapsed((v) => !v);

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col lg:flex-row pwa-app-shell max-lg:h-[100dvh] max-lg:max-h-[100dvh] max-lg:overflow-hidden">
      <AccountSettingsDialog open={accountOpen} onOpenChange={setAccountOpen} />
      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} groups={navGroups} quickActions={quickActions} />
      {IS_DEMO && (
        <div
          className="shrink-0 bg-amber-500 text-amber-950 text-center text-xs sm:text-sm py-2 px-4 font-medium z-50"
          data-testid="demo-environment-banner"
        >
          Training environment — practice freely. Data may reset overnight. Production:{" "}
          <a href="https://erp.bansalcerealproducts.com" className="underline font-semibold">
            erp.bansalcerealproducts.com
          </a>
        </div>
      )}
      {/* Desktop sidebar — expands to labels, collapses to 64px icon rail */}
      <aside
        className={cn(
          "hidden lg:flex shrink-0 bg-bansal-ink dark:bg-[hsl(222_22%_7%)] text-bansal-ivory flex-col sticky top-0 h-screen border-r border-white/[0.04] dark:border-white/[0.07] transition-[width] duration-200 ease-out",
          sidebarCollapsed ? "w-16" : "w-64",
        )}
        data-testid="sidebar"
        data-collapsed={sidebarCollapsed ? "true" : "false"}
      >
        <div
          className={cn(
            "border-b border-white/[0.06] flex items-center",
            sidebarCollapsed ? "px-2 pt-4 pb-3 justify-center" : "px-5 pt-6 pb-5 justify-start",
          )}
        >
          <BrandLogo variant={sidebarCollapsed ? "sidebarCollapsed" : "sidebar"} />
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden nav-scroll-dark">
          {sidebarCollapsed ? (
            <CollapsedNavRail items={visibleNav} />
          ) : (
            <div className="px-2.5 py-4">
              <NavGroupList groups={navGroups} pathname={location.pathname} linkClass={desktopNavClass} />
            </div>
          )}
        </nav>

        <div className={cn("border-t border-white/[0.06] mt-auto bg-black/15", sidebarCollapsed ? "p-2" : "p-4")}>
          {!sidebarCollapsed ? (
            <div className="flex items-center gap-3 mb-3">
              <UserAvatar user={user} rb={rb} size="sm" />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate font-medium">{user.name}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-bansal-ivory/45 mt-0.5">{user.role}</div>
              </div>
            </div>
          ) : (
            <TooltipProvider delayDuration={120}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex justify-center mb-2">
                    <UserAvatar user={user} rb={rb} size="sm" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-popover text-popover-foreground border border-border">
                  {user.name} · {user.role}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          <div className={cn("flex gap-1.5", sidebarCollapsed ? "flex-col items-center" : "flex-col")}>
            <TooltipProvider delayDuration={120}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={toggleSidebar}
                    className={cn(
                      "inline-flex items-center justify-center rounded-md border border-white/10 text-bansal-ivory/70 hover:text-bansal-ivory hover:bg-white/5 transition-colors",
                      sidebarCollapsed ? "h-11 w-11" : "w-full gap-2 text-xs py-2.5 min-h-10",
                    )}
                    aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    data-testid="sidebar-collapse-toggle"
                  >
                    {sidebarCollapsed ? (
                      <PanelLeftOpen className="h-4 w-4" />
                    ) : (
                      <>
                        <PanelLeftClose className="h-3.5 w-3.5" />
                        <span>Collapse</span>
                      </>
                    )}
                  </button>
                </TooltipTrigger>
                {sidebarCollapsed ? (
                  <TooltipContent side="right" className="bg-popover text-popover-foreground border border-border">
                    Expand sidebar
                  </TooltipContent>
                ) : null}
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider delayDuration={120}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setAccountOpen(true)}
                    className={cn(
                      "inline-flex items-center justify-center rounded-md border border-white/10 text-bansal-ivory/70 hover:text-bansal-ivory hover:bg-white/5 transition-colors",
                      sidebarCollapsed ? "h-11 w-11" : "w-full gap-2 text-xs py-2.5 min-h-10",
                    )}
                    data-testid="account-settings-button"
                    aria-label="Account settings"
                  >
                    <KeyRound className="h-3.5 w-3.5" />
                    {!sidebarCollapsed ? <span>Account settings</span> : null}
                  </button>
                </TooltipTrigger>
                {sidebarCollapsed ? (
                  <TooltipContent side="right" className="bg-popover text-popover-foreground border border-border">
                    Account settings
                  </TooltipContent>
                ) : null}
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider delayDuration={120}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={logout}
                    className={cn(
                      "inline-flex items-center justify-center rounded-md border border-white/10 text-bansal-ivory/70 hover:text-bansal-ivory hover:bg-white/5 transition-colors",
                      sidebarCollapsed ? "h-11 w-11" : "w-full gap-2 text-xs py-2.5 min-h-11",
                    )}
                    data-testid="logout-button"
                    aria-label="Sign out"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                    {!sidebarCollapsed ? <span>Sign out</span> : null}
                  </button>
                </TooltipTrigger>
                {sidebarCollapsed ? (
                  <TooltipContent side="right" className="bg-popover text-popover-foreground border border-border">
                    Sign out
                  </TooltipContent>
                ) : null}
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 min-h-0 max-lg:overflow-hidden">
        {/* Mobile app header + current-module sub-tabs */}
        <div className="lg:hidden sticky top-0 z-30 shadow-sm">
          <header className="flex items-center gap-3 px-4 py-3 bg-bansal-ink dark:bg-[hsl(222_22%_7%)] text-bansal-ivory border-b border-white/10 pt-[max(0.75rem,env(safe-area-inset-top))]">
            <BrandLogo variant="header" />
            <div className="flex-1 min-w-0">
              <h1 className="text-base font-semibold leading-tight truncate tracking-tight text-bansal-ivory">{pageTitle}</h1>
            </div>
            <NotificationsInbox
              align="end"
              triggerClassName="!border-white/15 !text-bansal-ivory/85 hover:!bg-white/10 hover:!text-white"
            />
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="shrink-0 rounded-full touch-manipulation"
                  aria-label="Account menu"
                  data-testid="app-header-menu"
                >
                  <UserAvatar user={user} rb={rb} size="sm" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="bottom"
                className="rounded-t-2xl p-0 bg-bansal-ink dark:bg-[hsl(222_22%_7%)] text-bansal-ivory border-t border-white/10 h-[min(92dvh,100%)] max-h-[92dvh] overflow-hidden flex flex-col gap-0 !pb-[env(safe-area-inset-bottom)] [&>button]:text-bansal-ivory [&>button]:opacity-80"
              >
                <SheetHeader className="shrink-0 px-4 pt-4 pb-2 text-left space-y-0">
                  <SheetTitle className="text-bansal-ivory font-serif text-lg">
                    {singleModuleNav ? "Menu" : "Modules"}
                  </SheetTitle>
                </SheetHeader>

                <div className="shrink-0 px-4 py-3 border-b border-white/10 space-y-3">
                  <div className="flex items-center gap-3">
                    <UserAvatar user={user} rb={rb} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{user.name}</div>
                      <div className="text-xs uppercase tracking-widest text-bansal-ivory/50">{user.role}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => { closeMobile(); logout(); }}
                      className="text-xs px-3 py-2 rounded-lg border border-white/15 text-bansal-ivory/80 min-h-10"
                    >
                      Sign out
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => { closeMobile(); setAccountOpen(true); }}
                    className="w-full text-xs px-3 py-2.5 rounded-lg border border-white/15 text-bansal-ivory/90 min-h-10"
                    data-testid="account-settings-mobile"
                  >
                    Account settings
                  </button>
                </div>

                <div className="shrink-0 px-2 py-1.5 border-b border-white/10">
                  <ThemeToggle variant="row" />
                </div>

                <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-0 py-3 nav-scroll-dark [-webkit-overflow-scrolling:touch]">
                  {singleModuleNav ? (
                    <div className="px-2 pb-6">
                      <NavGroupList
                        groups={navGroups}
                        pathname={location.pathname}
                        linkClass={mobileNavClass}
                        onItemClick={closeMobile}
                        mobile
                      />
                    </div>
                  ) : (
                    <MobileModuleMenu
                      key={mobileOpen ? "open" : "closed"}
                      groups={navGroups}
                      onNavigate={closeMobile}
                    />
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </header>

          {!singleModuleNav ? <MobileSubTabBar group={activeNavGroup} /> : null}
        </div>

        {/* Desktop top bar */}
        <div className="hidden lg:flex px-6 lg:px-8 py-3 border-b border-border bg-card/80 backdrop-blur-md items-center gap-4 sticky top-0 z-10">
          <div className="min-w-0 shrink-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-primary">
              <span className="inline-block h-px w-5 bg-bansal-gold/70" />
              Operations Console
            </div>
            <div className="font-serif text-xl text-foreground truncate leading-tight mt-0.5">{pageTitle}</div>
          </div>

          {/* Command-K search trigger */}
          <button
            type="button"
            onClick={() => setCmdOpen(true)}
            data-testid="command-search-trigger"
            className="hidden xl:flex items-center gap-2 flex-1 max-w-sm mx-auto px-3 h-9 rounded-lg border border-border bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Search className="h-4 w-4 shrink-0" />
            <span className="text-sm truncate">Search or jump to…</span>
            <kbd className="ml-auto inline-flex items-center rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground tracking-wider">
              ⌘K
            </kbd>
          </button>

          <div className="flex items-center gap-2.5 shrink-0 ml-auto xl:ml-0">
            <button
              type="button"
              onClick={() => setCmdOpen(true)}
              aria-label="Search"
              data-testid="command-search-icon"
              className="xl:hidden inline-grid place-items-center h-9 w-9 rounded-lg border border-border text-foreground/70 hover:text-foreground hover:bg-muted transition-colors"
            >
              <Search className="h-4 w-4" />
            </button>

            {quickActions.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="quick-actions-trigger"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-bansal-sage text-white text-sm font-medium hover:bg-bansal-sageDark transition-colors"
                  >
                    <Plus className="h-4 w-4" /> New
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Quick actions</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {quickActions.map((a) => (
                    <DropdownMenuItem
                      key={a.to}
                      onClick={() => navigate(a.to)}
                      data-testid={`quick-action-${a.label.replace(/\s+/g, "-").toLowerCase()}`}
                      className="gap-2 cursor-pointer"
                    >
                      {a.icon && <a.icon className="h-4 w-4 text-primary" />}
                      <span>{a.label}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <NotificationsInbox />
            <ThemeToggle />

            <span
              className={`hidden 2xl:inline-flex items-center gap-1.5 px-2.5 h-9 rounded-lg text-[11px] font-semibold uppercase tracking-[0.14em] ${rb.bg} ${rb.text}`}
              data-testid="role-badge"
            >
              {user.role}
            </span>

            <span className="hidden 2xl:inline-flex items-center gap-1.5 px-2.5 h-9 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-500/25 dark:text-emerald-300">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-medium">Live</span>
            </span>
            <span className="hidden xl:inline tabular text-xs text-muted-foreground">
              {formatIndiaDate(new Date().toISOString(), { weekday: "short", day: "numeric", month: "short" })}
            </span>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto lg:overflow-y-visible pwa-main-scroll">
          <div className="p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8 max-w-6xl mx-auto w-full">
            <Outlet key={location.pathname} />
          </div>
        </div>

        <AppBottomNav
          mode={singleModuleNav ? "direct" : "modules"}
          tabs={directTabs}
          home={singleModuleNav ? null : home}
          quickItems={singleModuleNav ? [] : quickNav}
          onMore={singleModuleNav ? null : () => setMobileOpen(true)}
        />
      </main>
    </div>
  );
}
